// Sürükleme sırasında KENAR OTOMATİK KAYDIRMA (edge auto-scroll).
//
// framer-motion `Reorder` kendi başına kenar kaydırma YAPMAZ; kart ekranın
// alt kenarına gelince liste durur ve kullanıcı kaydırma çubuğunu elle çekmek
// zorunda kalır. Bu kanca, sürükleme başladığında imleci izler ve kaydırılabilir
// kutunun üst/alt kenarına yaklaşıldıkça listeyi yumuşak bir eğriyle kaydırır.
//
// Kaydırılacak eleman SABİT DEĞİLDİR: sürükleme başlarken sürüklenen düğümden
// yukarı doğru aranır. Böylece tek kod dört senaryoyu da kapsar:
//   * kenar çubuğu (Sidebar)          * yüzen pencere (FloatingTabWindow)
//   * yüzen pencere büyütülmüş/tam     * HUD paneli (DraggablePanel)
// Hiçbiri bulunamazsa `window` kaydırılır.
import { useCallback, useEffect, useRef } from "react";

export const EDGE_PX = 70;      // kenar bölgesi kalınlığı
export const MAX_STEP = 13;     // maksimum px/kare — kontrollü, kart kaçmaz
export const MIN_STEP = 1.5;    // kenar bölgesine ilk girişte yavaş başlar

// Kenara olan mesafeye göre kare başına kaydırma miktarı. t² eğrisi: kenar
// bölgesine girişte yavaş, kenara yaklaştıkça hızlanır, MAX_STEP'i geçmez.
export const edgeSpeed = (dist) => {
  const t = Math.max(0, Math.min(1, (EDGE_PX - dist) / EDGE_PX));
  return MIN_STEP + (MAX_STEP - MIN_STEP) * t * t;
};

// Sürüklenen kartın KENDİSİ atlanır: TaskCard/SubtaskRow'da `resize: both` +
// `overflow: auto` var, aksi hâlde kaydırılacak eleman olarak seçilirdi.
export const isScrollable = (el) => {
  if (!el || el.nodeType !== 1) return false;
  const cs = window.getComputedStyle(el);
  if (cs.resize && cs.resize !== "none") return false;
  if (!/(auto|scroll)/.test(cs.overflowY)) return false;
  return el.scrollHeight - el.clientHeight > 4;
};

export const findScroller = (node) => {
  let el = node && node.nodeType === 1 ? node : null;
  while (el) {
    if (isScrollable(el)) return el;
    el = el.parentElement;
  }
  return null; // → window
};

export default function useDragAutoScroll() {
  const st = useRef({ active: false, raf: null, y: 0, el: null, cleanup: null });

  const stop = useCallback(() => {
    const s = st.current;
    s.active = false;
    if (s.raf) cancelAnimationFrame(s.raf);
    s.raf = null;
    s.el = null;
    s.onDelta = null;
    if (s.cleanup) { s.cleanup(); s.cleanup = null; }
  }, []);

  const start = useCallback((event, node, onScrollDelta) => {
    const s = st.current;
    if (s.active) return; // tekrarlı onDragStart → tek rAF döngüsü
    s.active = true;
    // framer-motion'ın `onDragStart` olayında `target` pencere olabilir; bu
    // yüzden sürüklenen DOM düğümü ayrıca geçirilir ve arama oradan başlar.
    s.el = findScroller(node || event?.target || null);
    s.y = event?.clientY ?? 0;
    s.onDelta = typeof onScrollDelta === "function" ? onScrollDelta : null;

    const onMove = (e) => { s.y = e.clientY; };
    const onKey = (e) => { if (e.key === "Escape") stop(); };
    const onHide = () => { if (document.hidden) stop(); };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    window.addEventListener("blur", stop);
    window.addEventListener("keydown", onKey);
    document.addEventListener("visibilitychange", onHide);
    s.cleanup = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      window.removeEventListener("blur", stop);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("visibilitychange", onHide);
    };

    const step = () => {
      const el = s.el;
      if (s.y) {
        // Kenar bölgesi, kutunun GÖRÜNEN kısmına göre hesaplanır: kapsayıcı
        // ekran dışına taşarsa (uzun kenar çubuğu / büyütülmüş pencere) fare
        // kutunun gerçek alt kenarına hiç ulaşamaz, bu yüzden viewport ile
        // kesişim alınır.
        let top = 0;
        let bottom = window.innerHeight;
        if (el) {
          const r = el.getBoundingClientRect();
          top = Math.max(r.top, 0);
          bottom = Math.min(r.bottom, window.innerHeight);
        }
        let delta = 0;
        if (s.y < top + EDGE_PX) delta = -edgeSpeed(s.y - top);
        else if (s.y > bottom - EDGE_PX) delta = edgeSpeed(bottom - s.y);
        // Kenar bölgesi dışında delta = 0 → kaydırma tamamen durur.
        if (delta) {
          // GERÇEKLEŞEN kaydırma ölçülür (liste başı/sonunda kırpılır) ve
          // sürüklenen elemana telafi olarak bildirilir: framer-motion drag
          // sırasında kaydırma ofsetini kendiliğinden telafi ETMEZ, kart
          // kaydırma miktarı kadar imleçten kopar.
          let applied = delta;
          if (el) {
            const before = el.scrollTop;
            el.scrollTop = before + delta;
            applied = el.scrollTop - before;
          } else {
            const before = window.scrollY;
            window.scrollBy(0, delta);
            applied = window.scrollY - before;
          }
          if (applied && s.onDelta) s.onDelta(applied);
        }
      }
      if (s.active) s.raf = requestAnimationFrame(step);
    };
    s.raf = requestAnimationFrame(step);
  }, [stop]);

  // Bileşen kaldırılırken döngü ve dinleyiciler kapatılır (sızıntı olmaz).
  useEffect(() => stop, [stop]);

  return { start, stop };
}
